import React, { useEffect, useState } from "react";
import {
  Dropdown,
  Button,
  Input,
  DatePicker,
  Select as AntdSelect,
} from "antd";
import {
  AddPrimaryIcon,
  CloseIcon,
  DeleteIcon,
  FilterIcon,
  IncidentIcon,
  MiniCalendarIcon,
} from "../../icons";
import "./style.scss";
import TagComp from "../tag";
import Select from "../select";
import { useDebounce } from "use-debounce";
import { useTickets } from "../../hooks/useTickets";
import { useCallTypes } from "../../hooks/useCallTypes";
import { useMembers } from "../../hooks/useMembers";

interface FilterDropdownProps {
  tableHeaders: { label: string; value: string }[];
  onChange?: (filters: {
    activeFilters: string[];
    dynamicFilters: DynamicFilter[];
    apiParams: Record<string, any>;
  }) => void;

  renderValueCustom?: (
    column: string,
    filter: DynamicFilter,
    idx: number,
    updateFilter: (index: number, key: keyof DynamicFilter, val: any) => void
  ) => React.ReactNode;
  optionsByColumn?: Record<string, string[]>;
  currentPage: number;
  perPage: number;
}

interface DynamicFilter {
  column: string | null;
  condition: string | null;
  value: any;
}

const conditionOptionsByColumn: Record<
  string,
  { label: string; value: string }[]
> = {
  id: [
    { label: "Is", value: "is" },
    { label: "Is not", value: "isNot" },
  ],
  srNo: [
    { label: "Is", value: "is" },
    { label: "Is not", value: "isNot" },
  ],
  name: [
    { label: "Is", value: "is" },
    { label: "Is not", value: "isNot" },
    { label: "Contain", value: "contain" },
    { label: "Not contain", value: "notContain" },
  ],
  callType: [
    { label: "Is", value: "is" },
    { label: "Is not", value: "isNot" },
    { label: "Contain", value: "contain" },
    { label: "Not contain", value: "notContain" },
  ],
  members: [
    { label: "Is", value: "is" },
    { label: "Is not", value: "isNot" },
    { label: "Contain", value: "contain" },
    { label: "Not contain", value: "notContain" },
  ],
  title: [
    { label: "Is", value: "is" },
    { label: "Is not", value: "isNot" },
    { label: "Contain", value: "contain" },
    { label: "Not contain", value: "notContain" },
  ],
  createdBy: [
    { label: "Is", value: "is" },
    { label: "Is not", value: "isNot" },
  ],
  status: [
    { label: "Is", value: "is" },
    { label: "Is not", value: "isNot" },
  ],
  assignedTo: [
    { label: "Is", value: "is" },
    { label: "Is not", value: "isNot" },
    { label: "Is empty", value: "isEmpty" },
    { label: "Is not empty", value: "isNotEmpty" },
  ],
  unitGroup: [
    { label: "Is", value: "is" },
    { label: "Is not", value: "isNot" },
    { label: "Contain", value: "contain" },
    { label: "Not contain", value: "notContain" },
  ],
  createdOn: [
    { label: "Is", value: "is" },
    { label: "Is not", value: "isNot" },
  ],
};

const statusOptions = [
  { label: "Closed", value: "3" },
  { label: "Resolved", value: "6" },
  { label: "Assigned / Pending", value: "10" },
  { label: "Ongoing", value: "24" },
  { label: "Completed", value: "25" },
  { label: "Unassigned", value: "1,3,6,10,24,25" },
];
const quickFilters = [
  {
    key: "createdLastWeek",
    label: "Created on last week",
    icon: <MiniCalendarIcon />,
  },
  { key: "animalDead", label: "Animal dead", icon: <IncidentIcon /> },
  { key: "illegalDumping", label: "Illegal dumping", icon: <IncidentIcon /> },
];

const ensureArray = (val: any) => (Array.isArray(val) ? val : val ? [val] : []);

const buildApiParams = (
  activeFilters: string[],
  dynamicFilters: DynamicFilter[],
  currentPage: number,
  perPage: number,
  callTypes: any[],
  isSupervisor: boolean
) => {
  const params: Record<string, any> = {
    type: "all",
    paginate: true,
    page: currentPage,
    perPage,
  };

  if (activeFilters.includes("createdLastWeek"))
    params["created_last_week"] = 1;

  const quickFilterMap: Record<string, string> = {
    illegalDumping: "Illegal dumping",
    animalDead: "Dead animal removal",
  };

  activeFilters.forEach((filterKey) => {
    const callTypeName = quickFilterMap[filterKey];
    if (!callTypeName) return;
    const svc = callTypes.find((s) => s.call_type === callTypeName);
    if (svc) {
      params["call_types_in"] = [
        ...(params["call_types_in"] || []),
        svc.service_id,
      ];
    }
  });

  dynamicFilters.forEach((f) => {
    if (!f.column || !f.condition) return;

    const arrayValue = ensureArray(f.value);
    switch (f.column) {
      case "id":
        if (f.condition === "is")
          params["ticket_ids_in"] = ensureArray(f.value);
        if (f.condition === "isNot")
          params["ticket_ids_not_in"] = ensureArray(f.value);
        break;
      case "title":
        if (arrayValue.length > 0) {
          if (f.condition === "is")
            params["call_types_in"] = JSON.stringify(arrayValue);
          else if (f.condition === "isNot")
            params["call_types_not_in"] = JSON.stringify(arrayValue);
          else if (f.condition === "contain")
            params["call_types_contain"] = JSON.stringify(arrayValue);
          else if (f.condition === "notContain")
            params["call_types_not_contain"] = JSON.stringify(arrayValue);
        }
        break;
      case "createdBy":
        if (f.condition === "is")
          params["created_by_in"] = ensureArray(f.value);
        if (f.condition === "isNot")
          params["created_by_not_in"] = ensureArray(f.value);
        break;
      case "status":
        // ✅ Supervisor tab single-status override
        if (isSupervisor) {
          if (f.condition === "is") params["status"] = f.value;
          if (f.condition === "isNot") params["status"] = f.value;
          break;
        } else {
          if (f.condition === "is") params["status_in"] = ensureArray(f.value);
          if (f.condition === "isNot")
            params["status_not_in"] = ensureArray(f.value);
          break;
        }
      case "assignedTo":
        if (f.condition === "is" && arrayValue.length > 0)
          params["assigned_to_in"] = JSON.stringify(arrayValue);
        else if (f.condition === "isNot" && arrayValue.length > 0)
          params["assigned_to_not_in"] = JSON.stringify(arrayValue);
        else if (f.condition === "isEmpty")
          params["assigned_to_is_empty"] = true;
        else if (f.condition === "isNotEmpty")
          params["assigned_to_is_not_empty"] = true;
        break;
      case "unitGroup":
        if (arrayValue.length > 0) {
          if (f.condition === "is")
            params["unit_groups_in"] = JSON.stringify(arrayValue);
          else if (f.condition === "isNot")
            params["unit_groups_not_in"] = JSON.stringify(arrayValue);
          else if (f.condition === "contain")
            params["unit_groups_contain"] = JSON.stringify(arrayValue);
          else if (f.condition === "notContain")
            params["unit_groups_not_contain"] = JSON.stringify(arrayValue);
        }
        break;

      case "createdOn":
        if (f.value) {
          const val = f.value;
          const dateStr =
            typeof val === "string"
              ? val
              : val?.format
              ? val.format("DD-MM-YYYY")
              : !isNaN(new Date(val).getTime())
              ? new Date(val).toISOString().split("T")[0]
              : null;

          if (!dateStr) break;

          if (f.condition === "is") params["created_date_in"] = [dateStr];
          if (f.condition === "isNot")
            params["created_date_not_in"] = [dateStr];
        }
        break;
    }
  });

  return params;
};

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  tableHeaders,
  onChange,
  renderValueCustom,
  optionsByColumn,
  currentPage,
  perPage,
}) => {
  const storedUserRaw = localStorage.getItem("Data");
  let storedUser: any = {};
  try {
    storedUser = storedUserRaw ? JSON.parse(storedUserRaw) : {};
  } catch (e) {
    storedUser = {};
  }

  const memberType = storedUser?.data?.member_type ?? storedUser?.membertype;

  // handle both shapes
  const isMemberType4 = Number(memberType) === 4;

  const SUPERVISOR_TABS = [
    { key: "new", label: "New Tickets", status: "28" },
    { key: "assigned", label: "Assigned Tickets", status: "-2" },
    { key: "myAssigned", label: "My Assigned Tickets", status: "10" }, // Not Initiated + Initiated
    { key: "resolved", label: "My Resolved Tickets", status: "6" },
    { key: "rejected", label: "My Rejected Tickets", status: "23" },
    { key: "parked", label: "My Parked Tickets", status: "31" },
  ];

  const BASE_TAB_ITEMS = [
    { key: "unassigned", label: "New", status: "1" },
    { key: "pending", label: "Not Initiated", status: "28" },
    { key: "ongoing", label: "Initiated", status: "10" },
    { key: "completed", label: "Parked", status: "31" },
    { key: "rejected", label: "Rejected", status: "23" },
    { key: "resolved", label: "Resolved", status: "6" },
    { key: "closed", label: "Closed", status: "3" },
  ];
  const getStatusOptions = (isSupervisor = false) => {
    const tabs = isSupervisor ? SUPERVISOR_TABS : BASE_TAB_ITEMS;
    return tabs.map((t) => ({
      label: t.label,
      value: t.status ?? t.key, // prefer status if available
    }));
  };
  const { data: callTypes = [], isLoading } = useCallTypes(); // ✅ top-level
  const { data: members = [] } = useMembers(1, "");
  const [visible, setVisible] = useState(false);

  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [dynamicFilters, setDynamicFilters] = useState<DynamicFilter[]>([]);
  const [ticketSearch, setTicketSearch] = useState("");
  const [assignedSearch, setAssignedSearch] = useState("");
  const [createdBySearch, setCreatedBySearch] = useState("");
  const [titleSearch] = useState("");
  const [unitGroupSearch, setUnitGroupSearch] = useState("");
  const [debouncedUnitGroupSearch] = useDebounce(unitGroupSearch, 300);
  const [debouncedTicketSearch] = useDebounce(ticketSearch, 300);
  const [debouncedAssignedSearch] = useDebounce(assignedSearch, 300);
  const [debouncedCreatedBySearch] = useDebounce(createdBySearch, 300);
  const [debouncedTitleSearch] = useDebounce(titleSearch, 300);

  // --- Separate IDs for inclusion/exclusion ---

  // const selectedTicketIdsIn = dynamicFilters
  //   .filter((f) => f.column === "id" && f.condition === "is" && f.value)
  //   .flatMap((f) => ensureArray(f.value));
  // const selectedTicketIdsNotIn = dynamicFilters
  //   .filter((f) => f.column === "id" && f.condition === "isNot" && f.value)
  //   .flatMap((f) => ensureArray(f.value));

  const { data: ticketData, isLoading: loadingTickets } = useTickets(
    {
      type: "all",
      perPage: 30,
      ...(debouncedTicketSearch
        ? { service_ticket_no: debouncedTicketSearch }
        : {}),
    },
    false
  );

  const ticketOptions =
    ticketData?.tickets?.map((t: any) => ({
      label: t.service_ticket_no || t.pikitup_ticket_id,
      value: t.pikitup_ticket_id,
    })) || [];

  const { data: assignedData, isLoading: loadingAssigned } = useTickets(
    {
      perPage: 30,
      type: "all",
      keyword: debouncedAssignedSearch || undefined,
    },
    false
  );

  const { data: createdByData, isLoading: loadingCreatedBy } = useTickets(
    {
      type: "all",
      perPage: 30,
      ...(debouncedCreatedBySearch
        ? { keyword: debouncedCreatedBySearch }
        : {}),
    },
    false
  );

  const { data: unitGroupData, isLoading: loadingUnitGroup } = useTickets(
    {
      type: "all",
      perPage: 30,
      ...(debouncedUnitGroupSearch
        ? { keyword: debouncedUnitGroupSearch }
        : {}),
    },
    false
  );

  const assignedOptions =
    members
      ?.map((t: any) => ({
        label: `${t.member_first_name} ${t.member_surname} `,
        value: t.pikitup_member_id,
      }))
      .filter((u: any) => u.label && u.value) || [];

  const uniqueAssignedOptions = Array.from(
    new Map(assignedOptions.map((u: any) => [u.value, u])).values()
  );

  const unitGroupOptions =
    unitGroupData?.tickets
      ?.map((t: any) => ({
        label: t.unit_group_name,
        value: t.unit_group_id,
      }))
      .filter((u: any) => u.label && u.value) || [];

  const uniqueUnitGroupOptions = Array.from(
    new Map(unitGroupOptions.map((u: any) => [u.value, u])).values()
  );

  const createdByOptions =
    createdByData?.tickets
      ?.map((t: any) => {
        const label = `${t?.customer_first_name || ""} ${
          t?.customer_surname || ""
        }`.trim();
        const value = t?.ticket_owner_id;

        // Only include entries with valid labels AND values
        if (label || value) {
          return { label, value };
        }
        return null;
      })
      .filter(Boolean) || [];

  const uniqueCreatedByOptions = Array.from(
    new Map(createdByOptions.map((u: any) => [u.value, u])).values()
  );

  // --- Update parent ---
  useEffect(() => {
    const apiParams = buildApiParams(
      activeFilters,
      dynamicFilters,
      currentPage,
      perPage,
      callTypes,
      isMemberType4
    );
    onChange?.({ activeFilters, dynamicFilters, apiParams });
  }, [activeFilters, dynamicFilters, callTypes, currentPage, perPage]);

  const addFilter = () =>
    setDynamicFilters([
      ...dynamicFilters,
      { column: null, condition: null, value: [] },
    ]);
  const clearAllFilters = () => {
    setActiveFilters([]);
    setDynamicFilters([]);
  };
  const deleteFilter = (index: number) =>
    setDynamicFilters(dynamicFilters.filter((_, i) => i !== index));
  const toggleFilter = (key: string) =>
    setActiveFilters(
      activeFilters.includes(key)
        ? activeFilters.filter((f) => f !== key)
        : [...activeFilters, key]
    );
  const removeFilter = (key: string) =>
    setActiveFilters(activeFilters.filter((f) => f !== key));
  const updateFilter = (index: number, key: keyof DynamicFilter, val: any) => {
    const updated = [...dynamicFilters];
    updated[index][key] = val;
    setDynamicFilters(updated);
  };

  const activeFilterCount = activeFilters.length + dynamicFilters.length;

  const renderValueSelect = (f: DynamicFilter, idx: number) => {
    if (!f.column || !f.condition) return null;

    // Use custom renderer if provided
    if (renderValueCustom) {
      const custom = renderValueCustom(f.column, f, idx, updateFilter);
      if (custom) return custom;
    }

    switch (f.column) {
      case "id":
        return (
          <AntdSelect
            mode="multiple"
            maxTagCount={1}
            maxTagPlaceholder={(omittedValues) =>
              `+${omittedValues.length} more`
            }
            showSearch
            allowClear
            filterOption={false}
            placeholder="Search Ticket ID"
            options={ticketOptions}
            value={f.value}
            onChange={(val) => updateFilter(idx, "value", val)}
            onSearch={setTicketSearch}
            onSelect={() => setTicketSearch("")} // 👈 reset search after selecting
            loading={loadingTickets}
            style={{ width: "100%" }}
          />
        );

      case "assignedTo":
        if (f.condition === "isEmpty" || f.condition === "isNotEmpty") {
          // No need to render user select for these two
          return null;
        }
        return (
          <AntdSelect
            mode="multiple"
            maxTagCount={1}
            maxTagPlaceholder={(omittedValues) =>
              `+${omittedValues.length} more`
            }
            showSearch
            allowClear
            filterOption={false}
            placeholder="Search user"
            options={uniqueAssignedOptions}
            value={f.value}
            onChange={(val) => updateFilter(idx, "value", val)}
            onSearch={setAssignedSearch}
            loading={loadingAssigned}
            style={{ width: "100%" }}
          />
        );
        return (
          <AntdSelect
            mode="multiple"
            showSearch
            allowClear
            filterOption={false}
            placeholder="Search user"
            options={uniqueAssignedOptions}
            value={f.value}
            onChange={(val) => updateFilter(idx, "value", val)}
            onSearch={setAssignedSearch}
            loading={loadingAssigned}
            style={{ width: "100%" }}
          />
        );
      case "unitGroup":
        // For contain / notContain → use Input
        if (f.condition === "contain" || f.condition === "notContain") {
          return (
            <Input
              value={f.value}
              onChange={(e) => updateFilter(idx, "value", [e.target.value])}
              placeholder="Enter unit group name"
            />
          );
        }

        // For is / isNot → use Select dropdown
        return (
          <AntdSelect
            mode="multiple"
            maxTagCount={1}
            maxTagPlaceholder={(omittedValues) =>
              `+${omittedValues.length} more`
            }
            showSearch
            allowClear
            filterOption={false}
            placeholder="Search unit group"
            options={uniqueUnitGroupOptions}
            value={f.value}
            onChange={(val) => updateFilter(idx, "value", val)}
            onSearch={setUnitGroupSearch}
            loading={loadingUnitGroup}
            style={{ width: "100%" }}
          />
        );
      case "createdBy":
        return (
          <AntdSelect
            className="w-full filter-select"
            mode="multiple"
            maxTagCount={1}
            maxTagPlaceholder={(omittedValues) =>
              `+${omittedValues.length} more`
            }
            showSearch
            allowClear
            filterOption={false}
            placeholder="Search created by"
            options={uniqueCreatedByOptions}
            value={f.value}
            onChange={(val) => updateFilter(idx, "value", val)}
            onSearch={setCreatedBySearch}
            loading={loadingCreatedBy}
            style={{ width: "100%" }}
          />
        );
      case "status":
        return (
          <Select
            options={getStatusOptions(isMemberType4)}
            value={f.value}
            onChange={(val) => updateFilter(idx, "value", val)}
            placeholder="Select status"
          />
        );

      case "createdOn":
        return (
          <DatePicker
            value={f.value || null}
            onChange={(val) => updateFilter(idx, "value", val)}
            placeholder="Select date"
            className="w-full filter-date-picker"
          />
        );
      default:
        return (
          <Input
            value={f.value}
            onChange={(e) => updateFilter(idx, "value", e.target.value)}
            placeholder="Enter value"
          />
        );
    }
  };

  const menu = (
    <div className="filter-dropdown">
      <div className="header-wrapper flex justify-between items-center">
        <h4 className="filter-title">Filter</h4>
        <div className="flex justify-center items-center">
          {" "}
          {(activeFilters.length > 0 || dynamicFilters.length > 0) && (
            <Button className="transparent clear-btn" onClick={clearAllFilters}>
              Clear all
            </Button>
          )}
          <span className="cursor-pointer" onClick={() => setVisible(false)}>
            <CloseIcon />
          </span>
        </div>
      </div>

      <div className="quick-filter-wrapper flex flex-col">
        <span className="title"></span>
        <div className="quick-filter flex items-center gap-3">
          {quickFilters.map((filter) =>
            activeFilters.includes(filter.key) ? (
              <TagComp
                key={filter.key}
                icon={filter.icon}
                onClose={() => removeFilter(filter.key)}
                className="ticket-type-tag quick-filter-tag"
              >
                {filter.label}
              </TagComp>
            ) : (
              <Button
                key={filter.key}
                icon={filter.icon}
                className="transparent ticket-type-btn quick-filter-btn"
                onClick={() => toggleFilter(filter.key)}
              >
                {filter.label}
              </Button>
            )
          )}
        </div>
      </div>

      <div className="custom-filters-wrapper">
        {dynamicFilters.length > 0 && (
          <div className="dynamic-filters-wrapper">
            <span className="title">All filters</span>
            {dynamicFilters.map((f, idx) => (
              <div key={idx} className="dynamic-filter flex gap-2 mb-2 ">
                <Select
                  className="column-filter w-full"
                  options={tableHeaders}
                  value={f.column}
                  onChange={(val) => {
                    updateFilter(idx, "column", val);
                    updateFilter(idx, "condition", null);
                    updateFilter(idx, "value", []);
                  }}
                  placeholder="Select column"
                />
                {f.column && (
                  <Select
                    className="condition-filter w-full"
                    options={conditionOptionsByColumn[f.column] || []}
                    value={f.condition}
                    onChange={(val) => {
                      updateFilter(idx, "condition", val);
                      updateFilter(idx, "value", []);
                    }}
                    placeholder="Condition"
                  />
                )}
                <div className="filter-value">{renderValueSelect(f, idx)}</div>
                <Button
                  icon={<DeleteIcon />}
                  className="transparent delete-btn"
                  onClick={() => deleteFilter(idx)}
                />
              </div>
            ))}
          </div>
        )}
        <div className="footer-btn-wrapper flex items-center justify-between">
          <Button
            onClick={addFilter}
            icon={<AddPrimaryIcon />}
            className="transparent add-filter-btn"
          >
            Add Filter
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <Dropdown
      open={visible}
      overlay={menu}
      onOpenChange={(val) => setVisible(val)}
      trigger={["click"]}
      placement="bottomRight"
      arrow={false}
      overlayClassName="filter-dropdown-wrapper"
    >
      <Button
        icon={<FilterIcon />}
        className={`transparent table-filter-btn ${
          activeFilterCount > 0 ? "active-filter-btn" : ""
        }`}
      >
        {activeFilterCount > 0 && (
          <span className="filter-count">{activeFilterCount}</span>
        )}
        Filter
      </Button>
    </Dropdown>
  );
};

export default FilterDropdown;
